import type { HTMLAttributes } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { COLOURS_AA } from '../Colours/index';

const Reset = `
margin: 0;
padding: 0;
border: 0;
outline: 0;
font-size: 100%;
vertical-align: baseline;
background: transparent;
`;

export const H1Base = styled.h1`
  ${Reset}
  font-family: 'Euclid Circular A';
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 40px;
  color: ${COLOURS_AA.text.SectionHeading};
`;

export const H2Base = styled.h2`
  ${Reset}
  font-family: 'Euclid Circular A';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
  color: ${COLOURS_AA.text.SectionHeading};
`;

export const H3Base = styled.h3`
  ${Reset}
  font-family: 'Euclid Circular A';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 28px;
  color: ${COLOURS_AA.text.SectionHeading};
`;

const H4Base = styled.h4`
  ${Reset}
  font-family: 'Euclid Circular A';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  color: ${COLOURS_AA.text.SectionHeading};
`;

const QuoteBase = styled.blockquote`
  ${Reset}
  font-family: Inter;
  font-style: italic;
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: 0.04em;
`;

export const BodyBase = styled.p`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: ${COLOURS_AA.text.StandardTxt};
`;

export const BodyStrongBase = styled.p`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  color: ${COLOURS_AA.text.StrongTxt};
`;

const SuggestionBase = styled.p`
  ${Reset}
  font-family: Inter;
  font-style: italic;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: ${COLOURS_AA.text.TextInputSuggestion};
`;

export const BtnAccessibleAABase = styled.span`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 28px;
`;

export const BtnSmallBase = styled.span`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
`;

const BtnXSmallBase = styled.span`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
`;

const BtnAddBase = styled.span`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 24px;
`;

const LabelBase = styled.label`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${COLOURS_AA.neutralBG.BgGrey700};
`;

export const LabelHeavyBase = styled.label`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: ${COLOURS_AA.neutralBG.BgGrey700};
`;

const CaptionBase = styled.caption`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  color: ${COLOURS_AA.text.InputMsgDefault};
`;

const SmallBase = styled.small`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  color: ${COLOURS_AA.text.InputMsgDefault};
`;

const InputDescriptionBase = styled.p`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${COLOURS_AA.text.InputDescription};
`;

const BodyWarningStrongBase = styled.span`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: ${COLOURS_AA.delete.BtnDeleteDefault};
`;

export const BylineBase = styled.p`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${COLOURS_AA.text.BylineTxt};
`;

export const BylineHeavyBase = styled.p`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 20px;
  color: ${COLOURS_AA.text.BylineHeavy};
`;

interface TextProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const H1 = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <H1Base {...rest} style={style}>
    {children}
  </H1Base>
);
export const H2 = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <H2Base {...rest} style={style}>
    {children}
  </H2Base>
);
export const H3 = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <H3Base {...rest} style={style}>
    {children}
  </H3Base>
);
export const H4 = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <H4Base {...rest} style={style}>
    {children}
  </H4Base>
);

export const Quote = ({
  children,
  style,
  cite,
}: TextProps & { cite: string }) => (
  <QuoteBase cite={cite} style={style}>
    {children}
  </QuoteBase>
);

export const Body = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <BodyBase {...rest} style={style}>
    {children}
  </BodyBase>
);

export const TableLink = styled(Link)`
  ${Reset}
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: ${COLOURS_AA.text.StandardTxt};
`;

export const BodyStrong = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <BodyStrongBase {...rest} style={style}>
    {children}
  </BodyStrongBase>
);

export const BodyWarningStrong = ({ children, style }: TextProps) => (
  <BodyWarningStrongBase style={style}>{children}</BodyWarningStrongBase>
);

export const Suggestion = ({ children, style }: TextProps) => (
  <SuggestionBase style={style}>{children}</SuggestionBase>
);

export const BtnAccessibleAA = ({ children, style }: TextProps) => (
  <BtnAccessibleAABase style={style}>{children}</BtnAccessibleAABase>
);

export const BtnSmall = ({ children, style }: TextProps) => (
  <BtnSmallBase style={style}>{children}</BtnSmallBase>
);

export const BtnXSmall = ({ children, style }: TextProps) => (
  <BtnXSmallBase style={style}>{children}</BtnXSmallBase>
);

export const BtnAdd = ({ children, style }: TextProps) => (
  <BtnAddBase style={style}>{children}</BtnAddBase>
);

export const Label = ({ children, style }: TextProps) => (
  <LabelBase style={style}>{children}</LabelBase>
);

export const LabelHeavy = ({ children, style }: TextProps) => (
  <LabelHeavyBase style={style}>{children}</LabelHeavyBase>
);

export const Caption = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <CaptionBase {...rest} style={style}>
    {children}
  </CaptionBase>
);

export const InputDescription = ({ children, style }: TextProps) => (
  <InputDescriptionBase style={style}>{children}</InputDescriptionBase>
);

export const Byline = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <BylineBase {...rest} style={style}>
    {children}
  </BylineBase>
);

export const BylineHeavy = ({
  children,
  style,
  ...rest
}: TextProps & HTMLAttributes<HTMLElement>) => (
  <BylineHeavyBase {...rest} style={style}>
    {children}
  </BylineHeavyBase>
);

export const Small = ({ children, style }: TextProps) => (
  <SmallBase style={style}>{children}</SmallBase>
);
